"use client";

import { useState, useMemo, useEffect, useRef, type MouseEvent } from "react";
import Image from "next/image";
import { BLOG_POSTS, BLOG_CATEGORIES, searchBlogPosts } from "@/lib/blog-posts";
import SiteNav from "@/components/SiteNav";
import Footer from "@/components/Footer";
import BlogHeroAnimation from "@/components/BlogHeroAnimation";

const T = {
  bg: "#FAFAF8",
  surface: "#FFFFFF",
  border: "#E8E6E1",
  borderLight: "#F0EEEA",
  text: "#1C1917",
  textSecondary: "#57534E",
  textMuted: "#78716C",
  primary: "#0E5F56",
  primaryLight: "#EEF8F5",
  primaryMedium: "#C6E7E1",
  primaryDark: "#093F39",
  sans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  serif: "'Lora', 'Georgia', 'Times New Roman', serif",
  r2: "10px",
  r3: "14px",
};

function formatDate(iso: string): string {
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

const categoryNames = Object.keys(BLOG_CATEGORIES);

/** "Enspryng (satralizumab)" → "Enspryng" for the decisions strip. */
function shortDrug(drug: string): string {
  return drug.split(" (")[0];
}
/** "Thyroid eye disease (TED)" → "Thyroid eye disease", trimmed to fit a pill.
 *  Case is kept as written, since many disease names are eponyms. */
function shortIndication(ind: string): string {
  const s = ind.split(" (")[0];
  return s.length > 30 ? s.slice(0, 28).replace(/[\s,]+\S*$/, "") + "…" : s;
}

export interface PostBadge { label: string; tone: "good" | "bad" | "next"; href: string }
export interface UpcomingDecision { drug: string; indication: string; dateLabel: string; decisionHref: string; postHref?: string }
export interface IndexFaq { q: string; a: string }
export interface ConditionFilter { slug: string; name: string; count: number }

/** Select value for posts that cover several conditions or none (roundups,
 *  the census, how-to guides). */
const ACROSS = "across-conditions";

const BADGE_TONE: Record<PostBadge["tone"], { bg: string; fg: string; border: string }> = {
  good: { bg: "#EDF8F1", fg: "#0B5A3E", border: "#A7F3D0" },
  bad: { bg: "#FEF2F2", fg: "#991B1B", border: "#FECACA" },
  next: { bg: "#FFFBEB", fg: "#92400E", border: "#FDE68A" },
};

/** Status chip rendered inside a card. Not a link, because the whole card
 *  already is one; the decision page is reachable from the post itself. */
function StatusBadge({ badge }: { badge?: PostBadge }) {
  if (!badge) return null;
  const t = BADGE_TONE[badge.tone];
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, color: t.fg, background: t.bg,
      border: `1px solid ${t.border}`, padding: "1px 8px", borderRadius: 12,
      fontFamily: T.sans, letterSpacing: "0.03em", textTransform: "uppercase" as const,
      whiteSpace: "nowrap" as const,
    }}>
      {badge.label}
    </span>
  );
}

export default function BlogIndexClient({ badges = {}, upcoming = [], faq = [], conditions = [], postCondition = {} }: {
  badges?: Record<string, PostBadge>;
  upcoming?: UpcomingDecision[];
  faq?: IndexFaq[];
  /** Conditions with at least 1 post, alphabetical. */
  conditions?: ConditionFilter[];
  /** Post slug → disease slug, for posts about one condition. */
  postCondition?: Record<string, string>;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeCondition, setActiveCondition] = useState<string | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const initializedRef = useRef(false);

  // Debounce search input (300ms like SymptomFinder)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle URL params on mount (?tag= seeds search, ?q= seeds search)
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const urlTag = params.get("tag");
    const urlQuery = params.get("q");
    const urlCondition = params.get("condition");
    if (urlCondition && (urlCondition === ACROSS || conditions.some((c) => c.slug === urlCondition))) {
      setActiveCondition(urlCondition);
    }
    if (urlQuery) {
      setSearchQuery(urlQuery);
      setDebouncedQuery(urlQuery);
    } else if (urlTag) {
      // Check if it maps to a category first
      const matchedCategory = categoryNames.find(cat =>
        BLOG_CATEGORIES[cat].some(t => t.toLowerCase() === urlTag.toLowerCase())
      );
      if (matchedCategory) {
        setActiveCategory(matchedCategory);
      } else {
        // Seed the search with the tag name
        setSearchQuery(urlTag);
        setDebouncedQuery(urlTag);
      }
    }
  }, []);

  const visible = useMemo(() => BLOG_POSTS.filter((p) => !p.hidden), []);

  // Apply search and/or category filter
  const filtered = useMemo(() => {
    let results = visible;

    // Apply search
    if (debouncedQuery.trim().length >= 2) {
      results = searchBlogPosts(debouncedQuery, results);
    }

    // Apply category filter
    if (activeCategory && BLOG_CATEGORIES[activeCategory]) {
      const matchTags = BLOG_CATEGORIES[activeCategory];
      results = results.filter(p =>
        p.tags.some(t => matchTags.includes(t))
      );
    }

    // Apply condition filter
    if (activeCondition === ACROSS) {
      results = results.filter((p) => !postCondition[p.slug]);
    } else if (activeCondition) {
      results = results.filter((p) => postCondition[p.slug] === activeCondition);
    }

    return results;
  }, [visible, debouncedQuery, activeCategory, activeCondition, postCondition]);

  // Sort by date (search already ranks by relevance, but within same score keep date order)
  const sorted = useMemo(() => {
    if (debouncedQuery.trim().length >= 2) {
      // Search results are already scored by relevance
      return filtered;
    }
    return [...filtered].sort((a, b) => b.date.localeCompare(a.date));
  }, [filtered, debouncedQuery]);

  // Any active filter switches the list to a uniform grid: no oversized
  // featured card and no full-width cards, so no post (and no condition)
  // looks promoted over another in a filtered view.
  const isFiltering = debouncedQuery.trim().length >= 2 || !!activeCategory || !!activeCondition;
  const activeConditionName = activeCondition === ACROSS
    ? "posts across conditions"
    : conditions.find((c) => c.slug === activeCondition)?.name;

  const handleConditionChange = (value: string) => {
    const next = value || null;
    setActiveCondition(next);
    // Keep the choice in the address so it can be shared or bookmarked.
    // /blog stays the canonical URL; ?condition= is a view of it.
    const url = new URL(window.location.href);
    if (next) url.searchParams.set("condition", next); else url.searchParams.delete("condition");
    window.history.replaceState(null, "", url.toString());
  };

  const handleCategoryClick = (cat: string | null) => {
    setActiveCategory(cat === activeCategory ? null : cat);
  };

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setDebouncedQuery("");
  };

  const isSearching = debouncedQuery.trim().length >= 2;
  // The 4 newest posts sit above the fold. Chip and condition filters only
  // narrow the list below them, so clicking a chip never moves the page.
  const leadPosts = useMemo(
    () => [...visible].sort((a, b) => b.date.localeCompare(a.date)).filter((p) => !p.skipFeatured).slice(0, 4),
    [visible],
  );
  const showTop = !isSearching && leadPosts.length > 0;
  const leadSlugs = new Set(showTop ? leadPosts.map((p) => p.slug) : []);
  const listPosts = isFiltering ? sorted : sorted.filter((p) => !leadSlugs.has(p.slug));
  const [lead, ...side] = leadPosts;

  const tagRow = (post: (typeof BLOG_POSTS)[number], size: number) => (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" as const }}>
      {post.tags[0] && (
        <span style={{ fontSize: size, fontWeight: 800, color: T.primary, letterSpacing: "0.07em", textTransform: "uppercase" as const }}>{post.tags[0]}</span>
      )}
      <time dateTime={post.date} style={{ fontSize: size + 1, color: T.textMuted }}>{formatDate(post.date)}</time>
      <StatusBadge badge={badges[post.slug]} />
    </div>
  );

  const cardImage = (post: (typeof BLOG_POSTS)[number], sizes: string, priority = false) =>
    post.heroAnimation ? (
      <BlogHeroAnimation size="card" />
    ) : post.image ? (
      <Image
        src={post.image}
        alt={post.imageAlt || post.title}
        width={1600}
        height={900}
        sizes={sizes}
        priority={priority}
        style={{
          width: "100%", height: "100%", display: "block",
          objectFit: post.imageFit || "cover",
          objectPosition: post.imagePosition || "center center",
          padding: post.imageFit === "contain" ? 12 : 0, boxSizing: "border-box" as const,
        }}
      />
    ) : null;

  const hoverOn = (e: MouseEvent<HTMLAnchorElement>) => {
    e.currentTarget.style.borderColor = T.primaryMedium;
    e.currentTarget.style.boxShadow = "0 6px 20px rgba(14,95,86,0.08)";
  };
  const hoverOff = (e: MouseEvent<HTMLAnchorElement>) => {
    e.currentTarget.style.borderColor = T.border;
    e.currentTarget.style.boxShadow = "none";
  };

  const chipStyle = (on: boolean) => ({
    fontSize: 13, fontWeight: on ? 700 : 500,
    color: on ? T.surface : T.textSecondary,
    background: on ? T.primary : T.surface,
    border: `1px solid ${on ? T.primary : "#D6D3CD"}`,
    padding: "6px 14px", borderRadius: 999,
    cursor: "pointer", fontFamily: T.sans, transition: "all 0.15s",
  });

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: T.sans }}>
      <SiteNav activePage="blog" />

      <style dangerouslySetInnerHTML={{ __html: `
        .ng-wrap { max-width: 1120px; margin: 0 auto; padding: 0 32px; }
        .ng-pills::-webkit-scrollbar { display: none; }
        .ng-track { display: flex; }
        .ng-dup { display: none; }
        @keyframes ngTicker { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @media (max-width: 900px) {
          .ng-head { flex-direction: column !important; align-items: stretch !important; }
          .ng-search { width: 100% !important; }
          .ng-top { grid-template-columns: 1fr !important; }
          .ng-list { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 640px) {
          .ng-wrap { padding: 0 16px; }
          .ng-head h1 { font-size: 30px !important; }
          .ng-head { padding-top: 22px !important; }
          .ng-search input { font-size: 16px !important; }
          .ng-strip-all { display: none !important; }
          /* Phones: the decisions run as a ticker, right to left, and pause
             while touched (Jason, October 28, 2026). The list is drawn twice
             so the loop is seamless; the copy is hidden from screen readers. */
          .ng-strip { flex-wrap: wrap; }
          .ng-pills { overflow: hidden !important; flex-basis: 100% !important; }
          .ng-track { width: max-content; animation: ngTicker var(--ng-ticker, 40s) linear infinite; }
          .ng-dup { display: flex; gap: 8px; padding-right: 8px; }
          .ng-pills:hover .ng-track, .ng-pills:active .ng-track, .ng-pills:focus-within .ng-track { animation-play-state: paused; }
        }
        @media (max-width: 640px) and (prefers-reduced-motion: reduce) {
          .ng-pills { overflow-x: auto !important; }
          .ng-track { animation: none; }
          .ng-dup { display: none; }
          .ng-lead-img { height: 200px !important; }
          .ng-lead h2 { font-size: 22px !important; }
          .ng-side-card { grid-template-columns: 96px 1fr !important; }
          .ng-list { grid-template-columns: 1fr !important; }
          .ng-filters select { width: 100% !important; margin-left: 0 !important; font-size: 16px !important; }
          .ng-filters .ng-rss { margin-left: 0 !important; }
        }
      `}} />

      <div className="ng-wrap">
        {/* Header: title on the left, search on the right */}
        <header className="ng-head" style={{
          display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 24,
          padding: "30px 0 18px", borderBottom: `1px solid ${T.border}`,
        }}>
          <div>
            <nav aria-label="Breadcrumb">
              <ol style={{ display: "flex", gap: 6, listStyle: "none", margin: "0 0 10px", padding: 0, fontSize: 13, color: T.textMuted }}>
                <li><a href="/" style={{ color: T.textMuted, textDecoration: "none" }}>Home</a></li>
                <li aria-hidden="true">/</li>
                <li style={{ color: T.textSecondary, fontWeight: 500 }} aria-current="page">News and Guides</li>
              </ol>
            </nav>
            <h1 style={{ fontSize: 40, lineHeight: 1.1, fontWeight: 700, color: T.text, fontFamily: T.serif, letterSpacing: "-0.025em", margin: "0 0 8px" }}>
              Rare Disease News and Guides
            </h1>
            <p style={{ fontSize: 16, color: T.textSecondary, lineHeight: 1.5, margin: 0, maxWidth: 600 }}>
              FDA decisions, clinical trials and treatments, explained in plain language for patients and caregivers.
            </p>
          </div>
          <div className="ng-search" style={{ position: "relative" as const, width: 340, flexShrink: 0 }}>
            <div style={{
              position: "absolute" as const, left: 14, top: "50%", transform: "translateY(-50%)",
              color: searchFocused ? T.primary : T.textSecondary, pointerEvents: "none", display: "flex",
            }}>
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder="Search a disease, drug or topic"
              aria-label="Search news and guides"
              style={{
                width: "100%", padding: "11px 38px 11px 40px", fontSize: 14, fontFamily: T.sans,
                color: T.text, background: T.surface,
                border: `1.5px solid ${searchFocused ? T.primary : "#A8A29E"}`, borderRadius: 12,
                outline: "none", boxSizing: "border-box" as const, textOverflow: "ellipsis" as const,
              }}
            />
            {searchQuery && (
              <button onClick={clearSearch} aria-label="Clear search" style={{
                position: "absolute" as const, right: 10, top: "50%", transform: "translateY(-50%)",
                background: "none", border: "none", color: T.textMuted, cursor: "pointer", padding: 4, display: "flex",
              }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        </header>

        {/* Next FDA decisions */}
        {showTop && upcoming.length > 0 && (
          <section aria-label="Next rare disease FDA decisions" className="ng-strip" style={{
            display: "flex", alignItems: "center", gap: 10,
            margin: "16px 0 22px", padding: "10px 14px", background: T.surface,
            border: `1px solid ${T.border}`, borderRadius: 12, fontSize: 13,
          }}>
            <h2 style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", color: T.primary, textTransform: "uppercase" as const, margin: "0 4px 0 0", whiteSpace: "nowrap" as const }}>
              <a href="/fda-calendar" style={{ color: "inherit", textDecoration: "none" }}>Next FDA decisions</a>
            </h2>
            <div className="ng-pills" style={{
              display: "flex", gap: 8, minWidth: 0, flex: "1 1 auto", overflowX: "auto" as const, scrollbarWidth: "none" as const,
              maskImage: "linear-gradient(to right, #000 92%, transparent)", WebkitMaskImage: "linear-gradient(to right, #000 92%, transparent)",
            }}>
              <div className="ng-track" style={{ ["--ng-ticker" as string]: `${Math.max(20, upcoming.length * 5)}s` }}>
                {[false, true].map((dup) => (
                  <div key={dup ? "dup" : "main"} className={dup ? "ng-dup" : undefined} aria-hidden={dup || undefined} style={dup ? undefined : { display: "flex", gap: 8, paddingRight: 8 }}>
                    {upcoming.map((u) => (
                      <a key={u.decisionHref} href={u.decisionHref} tabIndex={dup ? -1 : undefined} title={`${u.drug} for ${u.indication}`} style={{
                        border: `1px solid ${T.border}`, borderRadius: 999, padding: "4px 11px", background: T.bg,
                        color: T.text, textDecoration: "none", whiteSpace: "nowrap" as const, flexShrink: 0,
                      }}>
                        <b style={{ color: "#C2410C", marginRight: 6 }}>{u.dateLabel}</b>
                        <span style={{ fontWeight: 600 }}>{shortDrug(u.drug)}</span>
                        <span style={{ color: T.textMuted, margin: "0 5px" }}>·</span>
                        <span style={{ color: T.textSecondary }}>{shortIndication(u.indication)}</span>
                      </a>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <a href="/fda-calendar" className="ng-strip-all" style={{ marginLeft: "auto", color: T.primary, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap" as const }}>
              Full calendar →
            </a>
          </section>
        )}

        {/* Lead story plus the next 3 */}
        {showTop && lead && (
          <div className="ng-top" style={{ display: "grid", gridTemplateColumns: side.length ? "1.55fr 1fr" : "1fr", gap: 22, marginTop: upcoming.length ? 0 : 22 }}>
            <a href={`/blog/${lead.slug}`} className="ng-lead" onMouseEnter={hoverOn} onMouseLeave={hoverOff} style={{
              display: "block", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16,
              overflow: "hidden", textDecoration: "none", transition: "border-color 0.15s, box-shadow 0.2s",
            }}>
              <div className="ng-lead-img" style={{ height: 300, background: lead.imageBg || "#0A3D38", overflow: "hidden" }}>
                {cardImage(lead, "(max-width: 900px) 100vw, 660px", true)}
              </div>
              <div style={{ padding: "18px 22px 22px" }}>
                {tagRow(lead, 11)}
                <h2 style={{ fontSize: 27, lineHeight: 1.2, fontWeight: 700, color: T.text, fontFamily: T.serif, letterSpacing: "-0.02em", margin: "8px 0" }}>
                  {lead.title}
                </h2>
                <p style={{ margin: 0, color: T.textSecondary, fontSize: 15, lineHeight: 1.55 }}>{lead.excerpt}</p>
              </div>
            </a>
            {side.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column" as const, gap: 14 }}>
                {side.map((post) => (
                  <a key={post.slug} href={`/blog/${post.slug}`} className="ng-side-card" onMouseEnter={hoverOn} onMouseLeave={hoverOff} style={{
                    display: "grid", gridTemplateColumns: "120px 1fr", gap: 14, flex: "1 0 auto",
                    background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
                    overflow: "hidden", textDecoration: "none", transition: "border-color 0.15s, box-shadow 0.2s",
                  }}>
                    <div style={{ minHeight: 112, background: post.imageBg || "#0A3D38", overflow: "hidden" }}>
                      {cardImage(post, "120px")}
                    </div>
                    <div style={{ padding: "12px 14px 12px 0", alignSelf: "center" }}>
                      {tagRow(post, 10)}
                      <h3 style={{ fontSize: 16, lineHeight: 1.3, fontWeight: 700, color: T.text, fontFamily: T.serif, margin: "5px 0 0" }}>
                        {post.title}
                      </h3>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {/* All articles: topic chips, condition filter, then the list */}
        <main role="tabpanel" style={{ paddingBottom: 60 }}>
          <div className="ng-filters" style={{
            margin: showTop ? "30px 0 16px" : "22px 0 16px", paddingTop: showTop ? 20 : 0,
            borderTop: showTop ? `1px solid ${T.border}` : "none",
            display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" as const,
          }}>
            <h2 style={{ margin: "0 14px 0 0", fontSize: 20, fontWeight: 700, fontFamily: T.serif, color: T.text }}>
              {isSearching ? "Search results" : "All articles"}
            </h2>
            <div role="tablist" aria-label="Filter posts by topic" style={{ display: "flex", gap: 8, flexWrap: "wrap" as const }}>
              <button role="tab" aria-selected={activeCategory === null} onClick={() => handleCategoryClick(null)} style={chipStyle(activeCategory === null)}>
                All
              </button>
              {categoryNames.map((cat) => (
                <button key={cat} role="tab" aria-selected={activeCategory === cat} onClick={() => handleCategoryClick(cat)} style={chipStyle(activeCategory === cat)}>
                  {cat}
                </button>
              ))}
            </div>
            {conditions.length > 0 && (
              <select
                id="blog-condition"
                aria-label="Browse by condition"
                value={activeCondition || ""}
                onChange={(e) => handleConditionChange(e.target.value)}
                style={{
                  marginLeft: "auto", fontSize: 13, fontFamily: T.sans, color: T.text, background: T.surface,
                  border: `1px solid ${activeCondition ? T.primary : "#D6D3CD"}`, borderRadius: 8,
                  padding: "7px 10px", minWidth: 220, maxWidth: "100%", cursor: "pointer",
                }}
              >
                <option value="">All conditions</option>
                {conditions.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name} ({c.count})</option>
                ))}
                <option value={ACROSS}>Across conditions: roundups and guides ({visible.filter((p) => !postCondition[p.slug]).length})</option>
              </select>
            )}
          </div>

          {(isFiltering || (activeCondition && activeCondition !== ACROSS)) && (
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" as const, alignItems: "baseline", margin: "0 0 16px", fontSize: 13 }}>
              {isFiltering && (
                <span style={{ color: T.textMuted }}>
                  {sorted.length === 0
                    ? "No posts found. Try a different search or topic."
                    : `Showing ${sorted.length} post${sorted.length === 1 ? "" : "s"}${isSearching ? ` for "${debouncedQuery}"` : ""}${activeCategory ? ` in ${activeCategory}` : ""}${activeConditionName ? (activeCondition === ACROSS ? `, ${activeConditionName}` : ` about ${activeConditionName}`) : ""}`}
                </span>
              )}
              {activeCondition && activeCondition !== ACROSS && (
                <a href={`/trials/${activeCondition}`} style={{ fontWeight: 600, color: T.primary, textDecoration: "none" }}>
                  {activeConditionName} trials and treatments →
                </a>
              )}
            </div>
          )}

          {listPosts.length === 0 ? (
            <p style={{ fontSize: 15, color: T.textMuted, textAlign: "center" as const, padding: "40px 0" }}>
              No posts match. Try broadening your terms or clearing the filters above.
            </p>
          ) : (
            <div className="ng-list" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18 }}>
              {listPosts.map((post) => (
                <a key={post.slug} href={`/blog/${post.slug}`} onMouseEnter={hoverOn} onMouseLeave={hoverOff} style={{
                  display: "flex", flexDirection: "column" as const, background: T.surface,
                  border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden",
                  textDecoration: "none", transition: "border-color 0.15s, box-shadow 0.2s",
                }}>
                  {(post.image || post.heroAnimation) && (
                    <div style={{ height: 170, background: post.imageBg || "#0A3D38", overflow: "hidden" }}>
                      {cardImage(post, "(max-width: 640px) 100vw, (max-width: 900px) 50vw, 360px")}
                    </div>
                  )}
                  <div style={{ padding: "14px 18px 18px" }}>
                    {tagRow(post, 10)}
                    <h3 style={{ fontSize: 17, lineHeight: 1.3, fontWeight: 700, color: T.text, fontFamily: T.serif, letterSpacing: "-0.01em", margin: "7px 0 0" }}>
                      {post.title}
                    </h3>
                  </div>
                </a>
              ))}
            </div>
          )}

        {faq.length > 0 && (
          <section aria-labelledby="blog-faq" style={{ marginTop: 56, borderTop: `1px solid ${T.border}`, paddingTop: 28 }}>
            <h2 id="blog-faq" style={{ fontSize: 24, fontWeight: 700, color: T.text, fontFamily: T.serif, margin: "0 0 12px" }}>
              Questions about Trial Friend News and Guides
            </h2>
            {faq.map((f) => (
              <div key={f.q} style={{ borderTop: `1px solid ${T.borderLight}`, padding: "14px 0" }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.sans, margin: "0 0 6px" }}>{f.q}</h3>
                <p style={{ fontSize: 15, lineHeight: 1.65, color: T.textSecondary, fontFamily: T.sans, margin: 0 }}>{f.a}</p>
              </div>
            ))}
          </section>
        )}

        <p style={{ marginTop: 28, fontSize: 13.5, color: T.textMuted, fontFamily: T.sans }}>
          Use a feed reader? Every new post is in the{" "}
          <a href="/blog/rss.xml" style={{ color: T.primary, fontWeight: 600 }}>Trial Friend news and guides RSS feed</a>.
        </p>
        </main>
      </div>

      <Footer />
    </div>
  );
}
